import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { searchIncludes } from "@/lib/search-normalize";
import type { BroadcastRecipientReview } from "@shared/broadcast-recipient-review";

export type BroadcastReviewRow = BroadcastRecipientReview;

const STATUS_LABELS = {
  active: "Active",
  moving_out: "Moving out",
  inactive: "Inactive (former)",
  request: "Request or not moved in",
  unknown: "Unknown",
};

export function selectableRecipient(row: BroadcastReviewRow) {
  return row.eligible === true && row.group !== "excluded" && !row.propertyConflict && !row.duplicateRoom && !!row.reviewKey;
}

/** Only the first successful directory seeds defaults. Later arrivals never expand consent. */
export function reconcileRecipientSelection(rows: BroadcastReviewRow[], selected: number[], initialized: boolean, previousRows?: BroadcastReviewRow[]) {
  if (!initialized) return rows.filter((row) =>
    selectableRecipient(row) && row.group === "current" && row.defaultSelected === true,
  ).map((row) => row.id);
  const previous = new Map(previousRows?.map((row) => [row.id, row]));
  const allowed = new Set(rows.filter((row) => {
    if (!selectableRecipient(row)) return false;
    const before = previous.get(row.id);
    // A new warning is not the opt-in the host gave to the previous evidence.
    return row.group !== "needs_review" || !before || (before.group === "needs_review" && before.reviewKey === row.reviewKey);
  }).map((row) => row.id));
  return selected.filter((id) => allowed.has(id));
}

export function recipientGroups(rows: BroadcastReviewRow[]) {
  return {
    current: rows.filter((row) => row.group === "current"),
    needs_review: rows.filter((row) => row.group === "needs_review"),
    excluded: rows.filter((row) => row.group === "excluded"),
  };
}

export function recipientMatches(row: BroadcastReviewRow, search: string) {
  return [row.name, row.propertyName, row.roomName, row.roomNumber, STATUS_LABELS[row.lifecycleStatus]]
    .some((value) => searchIncludes(value, search.trim().toLowerCase()));
}

/** Dates are server-normalized property-local calendar dates: never parse as a local instant. */
export function RecipientDetails({ row }: { row: BroadcastReviewRow }) {
  const room = row.roomName?.trim() || row.roomNumber?.trim();
  return (
    <div className="min-w-0 space-y-1 break-words">
      <div className="text-sm font-medium">{row.name || `Resident #${row.id}`}</div>
      <div className="text-xs text-muted-foreground">
        {row.propertyName || (row.propertyId != null ? `Property #${row.propertyId}` : "Property unavailable")}
        {" · "}{STATUS_LABELS[row.lifecycleStatus] ?? "Unknown"}
        {" · "}{row.moveOutDate ? `Move-out: ${row.moveOutDate}` : "No move-out date on file"}
        {" · "}{room ? (/^room\b/i.test(room) ? room : `Room ${room}`) : "No room assigned"}
      </div>
      <div className="text-xs">{selectableRecipient(row) ? row.residencyEvidence === "unavailable" ? "Selectable from local records — live resident check required before sending." : "Eligible" : `Excluded — ${row.exclusionReason || "Not eligible for broadcast"}`}</div>
      {row.duplicateRoom && (
        <p className="text-xs text-amber-800 dark:text-amber-300">
          Shared-room record needs review — not selected. This label does not confirm a mismatch.
          {" "}The resident check identifies any blocking identity or membership issue.
        </p>
      )}
      {row.warning && <p className="text-xs text-amber-800 dark:text-amber-300">{row.warning}</p>}
      {row.group === "needs_review" && selectableRecipient(row) && (
        <p className="text-xs text-amber-800 dark:text-amber-300">Check this resident's details before explicitly including them.</p>
      )}
    </div>
  );
}

export function BroadcastRecipientPicker({
  rows, search, selectedIds, onToggle,
}: {
  rows: BroadcastReviewRow[];
  search: string;
  selectedIds: ReadonlySet<number>;
  onToggle: (id: number, shift: boolean) => void;
}) {
  const [showExcluded, setShowExcluded] = useState(false);
  const groups = recipientGroups(rows);
  const verificationPending = rows.some(row => row.residencyCheckState === "checking");
  const verificationUnavailable = rows.some(row => row.residencyCheckState === "failed" || row.residencyCheckState === "unavailable");
  const selected = rows.filter((row) => selectedIds.has(row.id) && selectableRecipient(row));
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground" data-testid="recipient-group-counts">
        {groups.current.length} current · {groups.needs_review.length} needs review · {groups.excluded.length} excluded
      </p>
      <div className="max-h-80 overflow-y-auto overscroll-contain rounded-md border p-2 space-y-4" data-testid="member-checkbox-list">
        {(["current", "needs_review", "excluded"] as const).map((group) => (
          <section key={group} aria-label={group === "current" ? "Current residents" : group === "needs_review" ? "Needs review" : "Excluded residents"}>
            {group === "excluded" ? (
              <Button type="button" variant="ghost" size="sm" aria-expanded={showExcluded} aria-controls="broadcast-excluded-rows" onClick={() => setShowExcluded((value) => !value)}>
                {showExcluded ? "Hide excluded" : "Show excluded"} ({groups.excluded.length})
              </Button>
            ) : <h3 className="text-sm font-medium mb-2">{group === "current" ? "Current residents" : "Needs review"} ({groups[group].length})</h3>}
            {(group !== "excluded" || showExcluded) && (
              <div id={group === "excluded" ? "broadcast-excluded-rows" : undefined} className="space-y-2">
                {groups[group].filter((row) => recipientMatches(row, search)).map((row) => (
                  <div key={row.id} className="flex items-start gap-2 rounded p-1" style={{ contentVisibility: "auto", containIntrinsicSize: "auto 110px" }}>
                    <Checkbox
                      className="mt-1 shrink-0"
                      checked={selectedIds.has(row.id) && selectableRecipient(row)}
                      disabled={!selectableRecipient(row)}
                      aria-label={`Include ${row.name || `resident ${row.id}`}`}
                      aria-describedby={`recipient-details-${row.id}`}
                      onClick={(event) => onToggle(row.id, event.shiftKey)}
                      data-testid={`checkbox-member-${row.id}`}
                    />
                    <div id={`recipient-details-${row.id}`} className="min-w-0 flex-1"><RecipientDetails row={row} /></div>
                  </div>
                ))}
                {!groups[group].some((row) => recipientMatches(row, search)) && <p className="text-xs text-muted-foreground p-1">{search.trim() ? "No matching residents in this group." : group === "current" && verificationPending ? "Verifying current residents. This is not an empty-house result." : group === "current" && verificationUnavailable ? "Current residents could not be verified. This does not mean the house is empty. Refresh resident evidence or check the PadSplit connection." : "No residents in this group."}</p>}
              </div>
            )}
          </section>
        ))}
      </div>
      <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1" role="status" data-testid="member-count-preview">
        <p><strong>{selected.length}</strong> of {rows.length} residents selected (across all search results)</p>
        <p className="text-xs text-muted-foreground">{selected.filter((row) => row.group === "current").length} current · {selected.filter((row) => row.group === "needs_review").length} explicitly included from Needs review</p>
      </div>
    </div>
  );
}

export function BroadcastFinalRecipientReview({ rows, confirmed, onConfirm, onBack }: {
  rows: BroadcastReviewRow[];
  confirmed: boolean;
  onConfirm: (confirmed: boolean) => void;
  onBack: () => void;
}) {
  return (
    <section className="rounded-md border bg-muted/30 p-3 space-y-3" aria-labelledby="final-recipient-review-title" data-testid="broadcast-final-recipient-review">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="final-recipient-review-title" className="text-sm font-medium">Final recipient review · {rows.length} selected</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>Back to edit recipients</Button>
      </div>
      <p className="text-xs text-muted-foreground">Only these residents will receive this broadcast. This is your audience review, not the message-token preview. Membership and access checks still apply before delivery.</p>
      <ul className="max-h-64 overflow-y-auto overscroll-contain space-y-3" aria-label="Final selected recipients">
        {rows.map((row) => <li key={row.id}><RecipientDetails row={row} /></li>)}
      </ul>
      <label className="flex items-start gap-2 text-sm">
        <Checkbox checked={confirmed} onCheckedChange={(value) => onConfirm(value === true)} data-testid="checkbox-final-recipient-confirm" />
        <span>I reviewed these exact recipients and their warnings. Include only this selection.</span>
      </label>
      <p className="text-xs text-muted-foreground">Nothing is sent until you press the final Send or Schedule button.</p>
    </section>
  );
}
